import { createSelector, Selector } from '@reduxjs/toolkit'
import { OrganisationDetail, EntityType, OrganisationType, PrimeBroker, EntityStatus, Organisation, OrganisationFeature } from 'api'
import { GlobalState } from 'store'
import {
  createAllEntityUpdatesSelector,
  createAllEntitiesSelector,
  createEntityGetterSelector,
  createAllEntitiesSortedSelector
} from '../entitySelectors'
import { currentOrganisationIdSelector } from '../adminSelectors'
import { createAuthorisedUserSelector } from '../connectionSelectors'
import { uniq } from 'lodash'
import { hasTradingRelationship, OrganisationHelper } from 'model'
import { isAdminGatewayUserSelector } from '../userSelectors'
import { isBuySideTraderSelector } from '../permissionSelectors'
import { isNotNil } from 'utils/predicate'
import { compareInsensitive, ParametricSelector } from 'utils'

export const createPrimeBrokersByOrganisationGetterSelector: Selector<
  GlobalState,
  (organisationId: string) => PrimeBroker[]
> = createSelector(createAllEntitiesSelector(EntityType.PRIME_BROKER), primeBrokers => (organisationId: string) =>
  primeBrokers.filter(primeBroker => primeBroker.organisationId === organisationId && primeBroker.status !== EntityStatus.DISABLED)
)

export const createPrimeBrokersByOrganisationSelector = <TProps>(
  organisationIdSelector: ParametricSelector<{}, TProps, string | null | undefined>
): ParametricSelector<GlobalState, TProps, string[]> =>
  createSelector([createAllEntitiesSelector(EntityType.PRIME_BROKER), organisationIdSelector], (primeBrokers, organisationId) =>
    primeBrokers
      .filter(
        primeBroker =>
          (organisationId ? primeBroker.organisationId === organisationId : true) && primeBroker.status !== EntityStatus.DISABLED
      )
      .map(primeBroker => primeBroker.id)
  )

export const organisationDetailsSelector: Selector<GlobalState, OrganisationDetail[]> = createSelector(
  createAllEntityUpdatesSelector(EntityType.ORGANISATION),
  createPrimeBrokersByOrganisationGetterSelector,
  (organisationEntries, getPrimeBrokersByOrganisation) =>
    organisationEntries.map(entry => ({
      organisation: entry.entity,
      primeBrokers: getPrimeBrokersByOrganisation(entry.entity.id),
      availableEvents: entry.availableEvents
    }))
)

export const hasCurrentOrganisationGridViewEnabledSelector: Selector<GlobalState, boolean> = createSelector(
  currentOrganisationIdSelector,
  createEntityGetterSelector(EntityType.ORGANISATION),
  (currentOrganisationId, getOrganisation) => {
    const organisation = currentOrganisationId ? getOrganisation(currentOrganisationId) : null

    return organisation ? organisation.enabledOrganisationFeatures?.includes?.(OrganisationFeature.ENABLE_GRID_VIEW) : false
  }
)

export const shouldShowGridViewTabSelector: Selector<GlobalState, boolean> = createSelector(
  currentOrganisationIdSelector,
  createEntityGetterSelector(EntityType.ORGANISATION),
  isAdminGatewayUserSelector,
  (currentOrganisationId, getOrganisation, isAdmin) => {
    if (isAdmin && isNotNil(currentOrganisationId)) return true

    const organisation = currentOrganisationId ? getOrganisation(currentOrganisationId) : null

    return organisation ? organisation.enabledOrganisationFeatures?.includes?.(OrganisationFeature.ENABLE_GRID_VIEW) : false
  }
)

export const hasCurrentOrganisationSmartGroupingEnabledSelector: Selector<GlobalState, boolean> = createSelector(
  currentOrganisationIdSelector,
  createEntityGetterSelector(EntityType.ORGANISATION),
  (currentOrganisationId, getOrganisation) => {
    const organisation = currentOrganisationId ? getOrganisation(currentOrganisationId) : null

    return organisation ? organisation.enabledOrganisationFeatures?.includes?.(OrganisationFeature.ENABLE_ORDER_SMART_GROUPING) : false
  }
)

export const hasCurrentOrganisationLockFixEnabledSelector: Selector<GlobalState, boolean> = createSelector(
  currentOrganisationIdSelector,
  createEntityGetterSelector(EntityType.ORGANISATION),
  (currentOrganisationId, getOrganisation) => {
    const organisation = currentOrganisationId ? getOrganisation(currentOrganisationId) : null

    return organisation ? organisation.enabledOrganisationFeatures?.includes?.(OrganisationFeature.ENABLE_FIX_LOCKING) : false
  }
)

export const isCurrentOrganisationSellSideSelector: Selector<GlobalState, boolean> = createSelector(
  currentOrganisationIdSelector,
  createEntityGetterSelector(EntityType.ORGANISATION),
  (currentOrganisationId, getOrganisation) => {
    const organisation = currentOrganisationId ? getOrganisation(currentOrganisationId) : null
    return organisation ? organisation.organisationTypes.includes(OrganisationType.SELL_SIDE) : true
  }
)

export const isCurrentOrganisationBuySideSelector: Selector<GlobalState, boolean> = createSelector(
  currentOrganisationIdSelector,
  createEntityGetterSelector(EntityType.ORGANISATION),
  (currentOrganisationId, getOrganisation) => {
    const organisation = currentOrganisationId ? getOrganisation(currentOrganisationId) : null
    return organisation ? organisation.organisationTypes.includes(OrganisationType.BUY_SIDE) : true
  }
)

export const organisationByShortCodeGetterSelector = createSelector(
  createAllEntitiesSelector(EntityType.ORGANISATION),
  allOrganisations => (shortCode: string) => allOrganisations.find(organisation => organisation.shortCode === shortCode) ?? null
)

export const organisationsByShortCodesGetterSelector = createSelector(
  createAllEntitiesSelector(EntityType.ORGANISATION),
  allOrganisations => (shortCodes: string[]) =>
    allOrganisations.filter(organisation =>
      shortCodes.map(shortCode => shortCode.toLowerCase()).includes(organisation.shortCode.toLowerCase())
    )
)

export const createOrganisationByIdGetterSelector: Selector<
  GlobalState,
  (organisationId: string) => Organisation | null
> = createSelector(createAllEntitiesSelector(EntityType.ORGANISATION), organisations => (organisationId: string) =>
  organisations.find(organisation => organisation.id === organisationId) || null
)

export const createOrganisationPrimeBrokerByIdGetterSelector: Selector<
  GlobalState,
  (organisationId: string) => PrimeBroker[] | null
> = createSelector(createAllEntitiesSelector(EntityType.PRIME_BROKER), primeBrokers => (organisationId: string) =>
  primeBrokers.filter(primeBroker => primeBroker.organisationId === organisationId && primeBroker.status === EntityStatus.ENABLED) || null
)

export function createCounterpartyOrganisationIdsSelector() {
  return createSelector(
    [
      isAdminGatewayUserSelector,
      isBuySideTraderSelector,
      createAuthorisedUserSelector(),
      createAllEntitiesSortedSelector(EntityType.ORGANISATION),
      createAllEntitiesSelector(EntityType.TRADING_RELATIONSHIP),
      createEntityGetterSelector(EntityType.ORGANISATION),
      createEntityGetterSelector(EntityType.TRADING_ENTITY)
    ],
    (isAdmin, isBuySideTrader, authorisedUser, sortedOrganisations, tradingRelationships, getOrganisation, getTradingEntity) => {
      if (isAdmin) {
        return sortedOrganisations.map(sortedOrganisation => sortedOrganisation.id)
      }

      const relationships = authorisedUser.tradingEntityDetails.reduce((relationshipsArray, { tradingEntityId }) => {
        return [
          ...relationshipsArray,
          ...tradingRelationships.filter(relationship =>
            hasTradingRelationship(
              isBuySideTrader ? tradingEntityId : relationship.buyTradingEntity,
              !isBuySideTrader ? tradingEntityId : relationship.sellTradingEntity,
              tradingRelationships,
              false,
              []
            )
          )
        ]
      }, [])

      return uniq(
        relationships
          .reduce((organisations, relationship) => {
            const tradingEntity = getTradingEntity(!isBuySideTrader ? relationship.buyTradingEntity : relationship.sellTradingEntity)
            if (isNotNil(tradingEntity)) {
              const organisationId = tradingEntity?.organisationId
              const organisation = isNotNil(organisationId) ? getOrganisation(organisationId) : undefined
              return isNotNil(organisation) ? [...organisations, organisation] : organisations
            }
            return organisations
          }, [])
          .sort((org1, org2) => compareInsensitive(OrganisationHelper.getName(org1), OrganisationHelper.getName(org2)))
          .map(organisation => organisation.id)
      )
    }
  )
}

export const createFilteredOrganisationsSelector = <TProps>(
  filtersSelector: ParametricSelector<{}, TProps, undefined | Array<(t: Organisation) => boolean>>
) =>
  createSelector([createAllEntitiesSelector(EntityType.ORGANISATION), filtersSelector], (organisations, filters = []) =>
    organisations
      .filter(organisation => filters.every(filter => filter(organisation)))
      .sort((o1, o2) => compareInsensitive(OrganisationHelper.getName(o1), OrganisationHelper.getName(o2)))
  )

export const allBuySideOrganisationIdsSelector: Selector<GlobalState, string[]> = createSelector(
  createAllEntitiesSortedSelector(EntityType.ORGANISATION),
  organisations => {
    return organisations
      .filter(organisation => organisation.organisationTypes.includes(OrganisationType.BUY_SIDE))
      .map(organisation => organisation.id)
  }
)

export const allSellSideOrganisationIdsSelector: Selector<GlobalState, string[]> = createSelector(
  createAllEntitiesSortedSelector(EntityType.ORGANISATION),
  organisations => {
    return organisations
      .filter(organisation => organisation.organisationTypes.includes(OrganisationType.SELL_SIDE))
      .map(organisation => organisation.id)
  }
)
